import { EnterFullScreenIcon, ExitFullScreenIcon } from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import {
  Decoration,
  DecorationSet,
  EditorView,
  Extension,
  ReactCodeMirrorRef,
  StateEffect,
  StateField,
} from "@uiw/react-codemirror";
import { diffLines } from "diff";
import { useEffect, useMemo, useRef, useState } from "react";
import { Label } from "react-aria-components";
import { Editor } from "tldraw";
import { useDebounceCallback } from "usehooks-ts";

import {
  CanvasEditor,
  CanvasEditorProps,
} from "@/components/canvas-editor/CanvasEditor";
import { CustomNodeShape } from "@/components/canvas-editor/shapes/shapes";
import { centerContent } from "@/components/canvas-editor/utils";
import { useEvent } from "@/components/hooks/useEvent";
import JsonEditor from "@/components/JsonEditor";
import { SimpleSelect } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Toggle } from "@/components/ui/toggle";
import {
  CustomNodeHistoryItem,
  previewCustomNodeQueryOptions,
} from "@/lib/api";
import { FeedVersion } from "@/lib/types";
import { cn } from "@/lib/utils";

interface DiffMarker {
  from: number;
  to: number;
  className: string;
}

function getChangeRanges(oldText: string, newText: string) {
  const changes = diffLines(oldText, newText);

  let oldPos = 0;
  let newPos = 0;

  const ranges: {
    deleted: { start: number; end: number; value: string }[];
    added: { start: number; end: number; value: string }[];
  } = {
    deleted: [],
    added: [],
  };

  changes.forEach((change) => {
    if (change.removed) {
      ranges.deleted.push({
        start: oldPos,
        end: oldPos + change.value.length,
        value: change.value,
      });
      oldPos += change.value.length;
    } else if (change.added) {
      ranges.added.push({
        start: newPos,
        end: newPos + change.value.length,
        value: change.value,
      });
      newPos += change.value.length;
    } else {
      oldPos += change.value.length;
      newPos += change.value.length;
    }
  });

  return ranges;
}

// Create a StateEffect for updating decorations
const addDiffHighlight = StateEffect.define<DiffMarker[]>();

// Create a StateField to manage the decorations
const diffHighlightField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    decorations = decorations.map(tr.changes);
    for (const e of tr.effects) {
      if (e.is(addDiffHighlight)) {
        decorations = Decoration.set(
          e.value.map((marker) =>
            Decoration.mark({
              class: marker.className,
            }).range(marker.from, marker.to),
          ),
          true,
        );
      }
    }
    return decorations;
  },
  provide: (f) => EditorView.decorations.from(f),
});

// Create the extension that includes our StateField
const createDiffExtension = (): Extension => {
  return [diffHighlightField];
};

function CustomNodeDiffEditor({
  title,
  feedVersion,
  className,
  feedNode,
  viewMode,
  codeMirrorRef,
}: {
  title: string;
  feedVersion: FeedVersion;
  className?: string;
  feedNode: CanvasEditorProps["rootNode"];
  viewMode: "canvas" | "json";
  codeMirrorRef: React.RefObject<ReactCodeMirrorRef>;
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const editorRef = useRef<Editor | undefined>(undefined);
  const onSetFullscreenChange = useEvent((value: boolean) => {
    setIsFullscreen(value);
    setTimeout(() => {
      if (editorRef.current) {
        centerContent(editorRef.current, { accountForSidebar: false });
      }
    }, 10);
  });

  return (
    <div
      className={cn(
        className,
        "flex flex-col",
        isFullscreen && "fixed inset-0 z-100000",
      )}
    >
      <div className="shrink-0 py-2 px-3 bg-slate-subtle rounded-t overflow-hidden border-b border-slate-normal flex items-center justify-between">
        <div className="font-semibold">{title}</div>

        <div className="flex items-center gap-0.5">
          <Toggle
            size="sm"
            aria-label={isFullscreen ? "Exit Full Screen" : "Enter Full Screen"}
            pressed={isFullscreen}
            onPressedChange={onSetFullscreenChange}
          >
            {isFullscreen ? (
              <ExitFullScreenIcon className="h-4 w-4" />
            ) : (
              <EnterFullScreenIcon className="h-4 w-4" />
            )}
          </Toggle>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        {viewMode === "canvas" ? (
          <CanvasEditor
            feedVersion={feedVersion}
            hideUi={true}
            setFullscreen={onSetFullscreenChange}
            ref={editorRef}
            rootNode={feedNode}
          />
        ) : (
          <JsonEditor
            feedVersion={feedVersion}
            codeMirrorRef={codeMirrorRef}
            extensions={[createDiffExtension()]}
          />
        )}
      </div>
    </div>
  );
}

export function CustomNodeDiff({
  shape,
  latestNode,
}: {
  shape: CustomNodeShape;
  latestNode: CustomNodeHistoryItem;
}) {
  // The values this install actually uses, sent to the server so both sides of
  // the diff show COMPILED logic rather than a template full of $PARAM
  // placeholders. What a user compares here is then what will really change in
  // their feed, produced by the same code that stores it.
  const values = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const parameter of shape.props.filter.metadata?.customNodeParameters ||
      []) {
      if (
        parameter?.name &&
        parameter.value !== undefined &&
        parameter.value !== null
      ) {
        out[parameter.name] = parameter.value;
      }
    }
    return out;
  }, [shape.props.filter.metadata?.customNodeParameters]);

  const { data: beforePreview } = useQuery(
    previewCustomNodeQueryOptions({
      component_id: shape.props.node.component_id,
      version: shape.props.node.version_id,
      values,
    }),
  );
  const { data: afterPreview } = useQuery(
    previewCustomNodeQueryOptions({
      component_id: latestNode.id,
      version: latestNode.version_id,
      values,
    }),
  );

  // Fall back to the templates while the previews load, or if the server cannot
  // compile a version (a detached install, say) — a diff of templates is less
  // useful than a diff of results, but it is better than an empty panel.
  const beforeValue = {
    manifest: { filter: beforePreview?.filter ?? shape.props.filter },
  } as FeedVersion;
  const afterValue = {
    manifest: { filter: afterPreview?.filter ?? latestNode.component },
  } as FeedVersion;

  const [viewMode, setViewMode] = useState<"canvas" | "json">("canvas");

  const beforeEditorRef = useRef<ReactCodeMirrorRef>(null!);
  const afterEditorRef = useRef<ReactCodeMirrorRef>(null!);

  const updateDiffHighlights = useDebounceCallback(() => {
    if (!beforeValue || !afterValue) return;

    const ranges = getChangeRanges(
      JSON.stringify(beforeValue, null, 2),
      JSON.stringify(afterValue, null, 2),
    );

    beforeEditorRef.current.view?.dispatch({
      effects: addDiffHighlight.of(
        ranges.deleted.map((r) => ({
          from: r.start,
          to: r.end,
          className: "diff-deletion",
        })),
      ),
    });

    afterEditorRef.current.view?.dispatch({
      effects: addDiffHighlight.of(
        ranges.added.map((r) => ({
          from: r.start,
          to: r.end,
          className: "diff-addition",
        })),
      ),
    });
  }, 300);

  useEffect(() => {
    if (viewMode === "json") {
      requestAnimationFrame(() => {
        void updateDiffHighlights();
      });
    }
  }, [viewMode, updateDiffHighlights]);

  return (
    <>
      <div className="flex flex-col gap-2">
        <Label htmlFor="view-mode">View Mode</Label>
        <SimpleSelect
          id="view-mode"
          aria-label="Select a view mode"
          options={[
            { label: "Canvas", value: "canvas" },
            { label: "JSON", value: "json" },
          ]}
          value={viewMode}
          onValueChange={(v) => setViewMode(v as "canvas" | "json")}
        />
      </div>
      <div className="border border-slate-normal rounded-md flex flex-1 overflow-hidden">
        <CustomNodeDiffEditor
          codeMirrorRef={beforeEditorRef}
          className="flex-1"
          title="Before"
          viewMode={viewMode}
          feedVersion={beforeValue}
          feedNode={{
            title: "Custom Node",
            variant: "custom-node",
            description:
              "This is a definition of a custom node that you can share with other feeds.",
            customNodeParameters:
              shape.props.filter.metadata?.customNodeParameters,
          }}
        />
        <Separator orientation="vertical" />
        <CustomNodeDiffEditor
          codeMirrorRef={afterEditorRef}
          title="After"
          className="flex-1"
          viewMode={viewMode}
          feedVersion={afterValue}
          feedNode={{
            title: "Custom Node",
            variant: "custom-node",
            description:
              "This is a definition of a custom node that you can share with other feeds.",
            customNodeParameters:
              latestNode.component.metadata?.customNodeParameters,
          }}
        />
      </div>
    </>
  );
}
